import React, { useState } from "react";
import {
  Box, TextField, Button, Typography, Paper, Link, Divider, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Stack
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import LockIcon from "@mui/icons-material/Lock";
import { useTranslation } from "react-i18next";
import MyAppHeader from "./MyAppHeader";
import api from "../services/api";
import { GoogleLogin } from "@react-oauth/google";
import { withCountryPrefix } from "./utils/routeHelpers";
import { trackEvent } from "../utils/analytics/gtag";
import AppleSignInButton from "./auth/AppleSignInButton";

const Login = () => {
  const { login, loginWithGoogle, loginWithApple } = useAuth();
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState(null);
  const [providers, setProviders] = useState({
    apple: false,
    google: false,
    facebook: false,
    apple_client_id: "",
    apple_redirect_uri: "",
  });

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const match = location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\b/);
  const country = match ? match[1] : "el";
  const query = new URLSearchParams(location.search || "");
  const returnToQuery = query.get("returnTo");
  const intentQuery = (query.get("intent") || "").trim().toLowerCase();
  const planQueryRaw = (query.get("plan") || "").trim().toLowerCase();
  const planQuery = planQueryRaw === "yearly" ? "yearly" : "monthly";
  const stateReturnTo = location.state?.returnTo;
  const fromState = location.state?.from;
  const fromPath = fromState
    ? `${fromState.pathname || ""}${fromState.search || ""}`
    : null;

  const isSafeInternalPath = (candidate) =>
    Boolean(candidate && typeof candidate === "string" && candidate.startsWith("/"));

  const resolvePostLoginTarget = () => {
    const fallback = withCountryPrefix("/client/dashboard", country);
    const premiumReturnTo = withCountryPrefix(
      `/subscription?intent=premium&plan=${planQuery}`,
      country
    );

    const premiumIntentExplicit =
      intentQuery === "premium" ||
      /(?:\?|&)intent=premium(?:&|$)/i.test(String(returnToQuery || ""));
    if (premiumIntentExplicit) {
      if (isSafeInternalPath(returnToQuery)) return returnToQuery;
      return premiumReturnTo;
    }

    const candidate = fromPath || stateReturnTo || returnToQuery;

    if (!isSafeInternalPath(candidate)) {
      return fallback;
    }

    const pathOnly = String(candidate).split("?")[0].toLowerCase();
    if (pathOnly.endsWith("/login") || pathOnly.endsWith("/register")) {
      return fallback;
    }

    return candidate;
  };

  const premiumPostAuthRedirect = resolvePostLoginTarget();

  const isPremiumIntent =
    intentQuery === "premium" ||
    /(?:\?|&)intent=premium(?:&|$)/i.test(String(returnToQuery || ""));
  const funnelParams = {
    lang: country,
    country,
    plan: planQuery,
    currency: null,
    intent: isPremiumIntent ? "premium" : null,
    user_tier: "guest",
  };

  React.useEffect(() => {
    trackEvent("funnel_login_view", funnelParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    let active = true;
    api.users
      .getAuthProviders()
      .then((response) => {
        if (!active) return;
        const payload = response?.data || {};
        setProviders({
          apple: Boolean(payload.apple),
          google: Boolean(payload.google),
          facebook: Boolean(payload.facebook),
          apple_client_id: String(payload.apple_client_id || "").trim(),
          apple_redirect_uri: String(payload.apple_redirect_uri || "").trim(),
        });
      })
      .catch(() => {
        if (!active) return;
        setProviders({
          apple: false,
          google: false,
          facebook: false,
          apple_client_id: "",
          apple_redirect_uri: "",
        });
      });
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    try {
      if (isPremiumIntent) {
        window.sessionStorage.setItem("postAuthRedirect", premiumPostAuthRedirect);
      } else {
        window.sessionStorage.removeItem("postAuthRedirect");
      }
    } catch (_err) {
      // non-blocking
    }
    return undefined;
  }, [isPremiumIntent, premiumPostAuthRedirect]);

  const consumePostAuthRedirect = () => {
    if (typeof window === "undefined") return null;
    try {
      const value = window.sessionStorage.getItem("postAuthRedirect");
      if (value) {
        window.sessionStorage.removeItem("postAuthRedirect");
      }
      if (value && typeof value === "string" && value.startsWith("/")) {
        return value;
      }
      return null;
    } catch (_err) {
      return null;
    }
  };

  // Email/Password change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prevForm) => ({ ...prevForm, [name]: value }));
  };

  // Email/Password submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const response = await login(form.email, form.password);
      if (response.success && response.data) {
        trackEvent("funnel_login_success", funnelParams);
        const { role } = response.data;
        if (role === "admin") {
          navigate("/admin/dashboard");
        } else {
          navigate(consumePostAuthRedirect() || resolvePostLoginTarget(), { replace: true });
        }

      } else {
        setError(response.message || t("login_failed"));
      }
    } catch (err) {
      setError(err.message || t("an_error_occurred"));
    }
  };

  // Google login handler
  const handleGoogleSuccess = async (credentialResponse) => {
    setError(null);
    try {
      const response = await loginWithGoogle(credentialResponse.credential, {
        desired_plan: isPremiumIntent ? "premium" : "free",
      });
      if (response.success && response.data) {
        trackEvent("funnel_login_success", funnelParams);
        const { role } = response.data;
        if (role === "admin") {
          navigate("/admin/dashboard");
        } else {
          navigate(consumePostAuthRedirect() || resolvePostLoginTarget(), { replace: true });
        }

      } else {
        setError(t("login_failed"));
      }
    } catch (err) {
      setError(err?.message || t("login_failed"));
    }
  };

  const handleAppleSuccess = async ({ idToken, user }) => {
    setError(null);
    try {
      const response = await loginWithApple({
        idToken,
        user,
        email: user?.email || null,
        desired_plan: isPremiumIntent ? "premium" : "free",
      });
      if (response.success && response.data) {
        trackEvent("funnel_login_success", funnelParams);
        const { role } = response.data;
        if (role === "admin") {
          navigate("/admin/dashboard");
        } else {
          navigate(consumePostAuthRedirect() || resolvePostLoginTarget(), { replace: true });
        }
      } else {
        setError(response.message || t("login_failed"));
      }
    } catch (err) {
      setError(err?.message || t("login_failed"));
    }
  };

  // Facebook login handler
  // const handleFacebookSuccess = async (response) => {
  //   setError(null);
  //   // Μερικές βιβλιοθήκες περνάνε το accessToken στο response.accessToken, άλλες στο response.tokenId
  //   const token = response.accessToken || response.tokenId;
  //   if (!token) {
  //     setError("Facebook login did not return an access token.");
  //     return;
  //   }
  //   try {
  //     const res = await loginWithFacebook(token);
  //     if (res.success) {
  //       trackEvent("funnel_login_success", funnelParams);
  //       navigate(consumePostAuthRedirect() || resolvePostLoginTarget(), { replace: true });
  //     } else {
  //       setError(res.message || t("login_failed"));
  //     }
  //   } catch (err) {
  //     setError(err.message || t("login_failed"));
  //   }
  // };

  // Password reset
  const handleResetPassword = async () => {
    setResetMessage("");
    setResetError("");
    setResetLoading(true);
    try {
      await api.users.requestPasswordReset(resetEmail);
      setResetMessage(
        t("reset_sent_message") || "If the email exists, we sent a reset link."
      );
    } catch (err) {
      // Keep response generic to avoid account-enumeration leakage.
      setResetMessage(
        t("reset_sent_message") || "If the email exists, we sent a reset link."
      );
    } finally {
      setResetLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setResetDialogOpen(true);
    setResetEmail("");
    setResetMessage("");
    setResetError("");
  };

  return (
    <>
      <MyAppHeader />
      <Box sx={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Paper elevation={4} sx={{
          p: 4, maxWidth: 400, width: "100%", borderRadius: 4, boxShadow: "0 6px 36px 0 #49bc9050"
        }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <LockIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {t("login_title") || "Login"}
            </Typography>
          </Box>
          {isPremiumIntent && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {(t("premium_one_step_banner") || "You're 1 step away from Premium")}{" "}
              <strong>({planQuery === "yearly" ? "Yearly" : "Monthly"})</strong>
            </Alert>
          )}
          {error && <Typography color="error" align="center" sx={{ mb: 2 }}>{error}</Typography>}

          {/* --- SOCIAL LOGIN --- */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 2,
              width: "100%",
            }}
          >
            <Stack direction="column" spacing={2} sx={{ width: "100%" }}>
              {providers.google ? (
                <Box
                  sx={{
                    width: "100%",
                    "& iframe": { width: "100% !important" },
                    "& > div": { width: "100% !important" }, // το outer div που βάζει η βιβλιοθήκη
                    "& > div > div": { width: "100% !important" }, // καμιά φορά έχει nested
                  }}
                >
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => setError(t("google_login_failed"))}
                    useOneTap={false}
                  />
                </Box>
              ) : null}
              {providers.apple ? (
                <AppleSignInButton
                  label={t("login_with_apple")}
                  onSuccess={handleAppleSuccess}
                  onError={(err) => setError(err?.message || t("apple_login_failed"))}
                  clientId={providers.apple_client_id}
                  redirectURI={providers.apple_redirect_uri}
                />
              ) : null}
              {/* <FacebookRedirectLogin
                onSuccess={handleFacebookSuccess}
                onFailure={() => setError("Facebook Login failed")}
                postAuthRedirect={isPremiumIntent ? premiumPostAuthRedirect : null}
              >
                {t("login_with_facebook") || "Σύνδεση με Facebook"}
              </FacebookRedirectLogin> */}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t("or_login_with_email") || "ή είσοδος με email"}
            </Typography>
          </Box>


          <Divider sx={{ my: 2 }} />

          <form onSubmit={handleSubmit}>
            <TextField
              label={t("email") || "Email"}
              name="email"
              type="email"
              margin="normal"
              fullWidth
              value={form.email}
              onChange={handleChange}
              required
              autoFocus
            />
            <TextField
              label={t("password") || "Password"}
              name="password"
              type="password"
              margin="normal"
              fullWidth
              value={form.password}
              onChange={handleChange}
              required
            />
            <Button variant="contained" color="primary" type="submit" fullWidth sx={{ mt: 2 }}>
              {t("login_btn") || "Login"}
            </Button>
          </form>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Link
              component="button"
              underline="hover"
              sx={{ cursor: "pointer" }}
              onClick={handleOpenDialog}
            >
              {t("forgot_password") || "Ξέχασες τον κωδικό;"}
            </Link>
            <Link
              component="button"
              underline="hover"
              onClick={() => {
                const params = new URLSearchParams(location.search || "");
                const returnTo = params.get("returnTo");
                const intent = params.get("intent");
                const plan = params.get("plan");
                const registerPath = withCountryPrefix("/register", country);
                const qs = new URLSearchParams();
                if (intent) qs.set("intent", intent);
                if (plan) qs.set("plan", plan);
                if (returnTo) qs.set("returnTo", returnTo);
                navigate(qs.toString() ? `${registerPath}?${qs.toString()}` : registerPath);
              }}
            >
              {t("no_account_register") || "Δεν έχεις λογαριασμό; Εγγραφή"}
            </Link>
          </Box>
        </Paper>
        {/* Password reset dialog */}
        <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
          <DialogTitle>{t("reset_password") || "Reset Password"}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              {t("reset_instruction") || "Enter your email to receive reset instructions."}
            </Typography>
            <TextField
              label={t("email") || "Email"}
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              fullWidth
              autoFocus
              disabled={resetLoading}
            />
            {resetMessage && (
              <Typography color="success.main" sx={{ mt: 2 }}>
                {resetMessage}
              </Typography>
            )}
            {resetError && (
              <Typography color="error" sx={{ mt: 2 }}>
                {resetError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetDialogOpen(false)} disabled={resetLoading}>
              {t("cancel") || "Cancel"}
            </Button>
            <Button
              onClick={handleResetPassword}
              variant="contained"
              disabled={!resetEmail || resetLoading}
            >
              {resetLoading ? t("sending") || "Sending..." : t("send") || "Send"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default Login;
